import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { fetchCrops, fetchSymptomsByLevel } from '../api/cropService';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../api/languageHelper';
import { getaccesstoken } from '../context/AuthContext';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const EditCrop = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
  });
  const [newImage, setNewImage] = useState(null);
  const [l1Symptoms, setL1Symptoms] = useState([]);
  const [deletedSymptoms, setDeletedSymptoms] = useState([]);

  useEffect(() => {
    const loadCrops = async () => {
      const data = await fetchCrops(true);
      setCrops(data);
      if (data.length > 0) {
        selectCrop(data[0]);
      }
    };
    loadCrops();
  }, []);

  const selectCrop = async (crop) => {
    setSelectedCrop(crop);
    setFormData({
      name: crop.name || '',
      description: crop.description || '',
      imageUrl: crop.imageUrl || '',
    });
    const l1Data = await fetchSymptomsByLevel(crop.cid, 1, true);
    setL1Symptoms(l1Data);
  };
  // Add a new symptom section
  const addSymptom = () => {
    setL1Symptoms([...l1Symptoms, { name: '', description: '', question: '' }]);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setNewImage(file);
  };

  const handleSymptomChange = (index, e) => {
    const updated = [...l1Symptoms];
    updated[index][e.target.name] = e.target.value;
    setL1Symptoms(updated);
  };

  const removeSymptom = (index) => {
    const symptom = l1Symptoms[index];
    if (symptom.sid) {
      setDeletedSymptoms((prev) => [...prev, symptom]);
    }
    const updated = [...l1Symptoms];
    updated.splice(index, 1);
    setL1Symptoms(updated);
  };

  // Helper to re-fetch crop and its symptoms
  const refreshCropData = async (cid) => {
    try {
      const cropsData = await fetchCrops(true);
      setCrops(cropsData);
      const crop = cropsData.find(c => c.cid === cid);
      if (crop) {
        setSelectedCrop(crop);
        setFormData({
          name: crop.name || '',
          description: crop.description || '',
          imageUrl: crop.imageUrl || '',
        });
        const l1Data = await fetchSymptomsByLevel(cid, 1, true);
        setL1Symptoms(l1Data);
      }
    } catch (err) {
      console.error('Error refreshing crop data:', err);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCrop) return;

    const headers = {
      'auth-token': getaccesstoken(),
    };

    try {
      const locale = getCurrentLanguage();
       // --- STEP 1: Handle image upload ---
       let imageUrl = formData.imageUrl;
       if (newImage) {
         const imgForm = new FormData();
         imgForm.append('image', newImage);
         const uploadRes = await axiosInstance.post(`${apiPrefix}upload`, imgForm, { headers });
         const { url, imageKitId } = uploadRes.data;

         imageUrl = url;

         // Register image in DB
         await axiosInstance.post(
           `${apiPrefix}images`,
           {
             id: selectedCrop.cid,
             type: 'Crop',
             imageUrl: url,
             imageKitId: imageKitId,
           },
           { headers: { ...headers, 'Content-Type': 'application/json' } }
         );
       }

       // --- STEP 2: Update the crop ---
       const cropPayload = {
         name: formData.name,
         description: formData.description,
         imageUrl: Array.isArray(formData.imageUrl) ? formData.imageUrl[0] || '' : formData.imageUrl,
         locale_code: locale,
       };

       const cropRes = await axiosInstance.put(`${apiPrefix}crops/${selectedCrop.cid}`, cropPayload, { headers });

      // --- STEP 3: Update L1 symptoms ---
      for (const symptom of l1Symptoms) {
        if (symptom.sid == null) continue; // skip new symptoms
        const symptomPayload = {
          parent_id: -1,
          grandparent_id: 0,
          level: 1,
          locale_code: locale,
          name: symptom.name,
          description: symptom.name,
          question: symptom.qn || '',
        };

        await axiosInstance.put(
          `${apiPrefix}symptom/${selectedCrop.cid}/${symptom.sid}`,
          symptomPayload,
          { headers }
        );
      }

      // --- STEP 4: Add new symptoms ---
      const newSymptoms = l1Symptoms.filter(s => !s.sid);
      if (newSymptoms.length > 0) {
        const symptomPayloads = newSymptoms.map(symptom => ({
          level: 1,
          cid: selectedCrop.cid,
          sid: undefined,
          locale_code: locale,
          name: symptom.name,
          description: symptom.description,
          qn: symptom.qn,
        }));
        await axiosInstance.post(
          `${apiPrefix}symptom/array/post`,
          symptomPayloads,
          { headers }
        );
      }

      // --- STEP 5: Delete removed symptoms ---
      for (const symptom of deletedSymptoms) {
        const symptomId = symptom.sid;
        if (!symptomId) continue;

        await axiosInstance.delete(
          `${apiPrefix}symptom/${selectedCrop.cid}/${symptomId}`,
          { headers }
        );
      }
      setDeletedSymptoms([]); // clear after successful save

      if (cropRes.data === 'done') {
        alert(t('expert.editCrop.successMessage'));
      } else {
        alert(t('expert.editCrop.unexpectedResponse') + cropRes.data);
      }
    } catch (error) {
      console.error('Error updating crop:', error.response?.data || error.message);
      alert(t('expert.editCrop.failMessage'));
    } finally {
      // Always refresh after submit — ensures UI matches DB
      await refreshCropData(selectedCrop.cid);
    }
  };

  return (
    <div className="flex p-6 space-x-6">
      {/* LEFT: Edit Form */}
      <div className="w-2/3 bg-white p-4 rounded shadow">
        {selectedCrop && (
          <>
            <h2 className="text-xl font-semibold mb-4">{t('expert.editCrop.pageTitle')}</h2>

            <label className="block mb-3">
              {t('expert.editCrop.cropNameLabel')}
              <input
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                className="w-full border p-2 rounded"
              />
            </label>

            <label className="block mb-3">
              {t('expert.editCrop.cropDescriptionLabel')}
              <textarea
                name="description"
                value={formData.name}
                onChange={handleFormChange}
                className="w-full border p-2 rounded"
              />
            </label>

            <div className="mb-4">
              <label className="block">{t('expert.editCrop.cropImageLabel')}</label>
              {formData.imageUrl && (
                <img src={formData.imageUrl} alt="Crop" className="w-32 h-32 object-cover mt-2 rounded" />
              )}
            </div>

            <label className="block mb-4">
              {t('expert.editCrop.chooseImageLabel')}
              <input type="file" onChange={handleImageChange} className="block mt-2" />
            </label>

            <h3 className="text-lg font-semibold mt-6 mb-3">{t('expert.editCrop.l1SymptomsTitle')}</h3>
            {l1Symptoms.map((symptom, index) => (
              <div key={symptom.symptomId || index} className="border p-3 mt-2 rounded bg-gray-50 space-y-2">
                <div>
                  <label>{t('expert.editCrop.l1SymptomNameLabel')}</label>
                  <input
                    name="name"
                    value={symptom.name}
                    onChange={(e) => handleSymptomChange(index, e)}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label>{t('expert.editCrop.l1SymptomDescriptionLabel')}</label>
                  <input
                    name="description"
                    value={symptom.name}
                    onChange={(e) => handleSymptomChange(index, e)}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label>{t('expert.editCrop.l1SymptomQuestionLabel')}</label>
                  <input
                    name="qn"
                    value={symptom.qn}
                    onChange={(e) => handleSymptomChange(index, e)}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeSymptom(index)}
                  className="text-red-500 mt-2"
                >
                  {t('expert.editCrop.deleteSymptom')}
                </button>
              </div>
            ))}
            {/* Add Symptom Button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={addSymptom}
                className="text-blue-500"
              >
                {t('expert.addCrop.addAnotherSymptom')}
              </button>
            </div>

            <button
              onClick={handleSubmit}
              className="mt-6 w-full bg-blue-500 text-white p-3 rounded"
            >
              {t('expert.editCrop.submitButton')}
            </button>
          </>
        )}
      </div>

      {/* RIGHT: Existing Crops */}
      <div className="w-1/3 bg-gray-100 p-4 rounded shadow">
        <h2 className="font-bold text-lg mb-4">{t('expert.editCrop.existingCropsTitle')}</h2>
        {crops.map((crop) => (
          <div
            key={crop.cid}
            onClick={() => selectCrop(crop)}
            className={`p-2 cursor-pointer rounded mb-2 ${selectedCrop?.cid === crop.cid ? 'bg-blue-200' : ''
              }`}
          >
            {crop.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditCrop;